import { inject, Injectable } from '@angular/core';
import { AdminUserService } from './admin-user.service';
import { UserDataService } from './userData.service';
import { ServerStatus } from '../models/types.model';
import { UserEvent } from '../models/global.model';
import { ICurrentAdminUser } from '../models/admin-access.model';

// Les droits de l'utilisateur courant, chargés une fois après la connexion.
//
// Ils viennent du backend et non du token : le front n'a pas à savoir ce qu'un
// groupe Cognito autorise, et un droit ajouté côté serveur n'exige pas de
// livraison front. Ce qui est caché ici n'est qu'un confort — le refus qui
// compte est celui de la passerelle.
const ALL_PERMISSIONS = '*';

@Injectable({
  providedIn: 'root'
})
export class PermissionService {
  private adminUserService = inject(AdminUserService);
  private userService = inject(UserDataService);

  private current: ICurrentAdminUser | null = null;
  private loading: Promise<void> | null = null;

  constructor() {
    // On écoute l'événement plutôt que d'appeler PermissionService depuis
    // AuthService : celui-ci hérite de HttpService, et l'importer ici créerait
    // un cycle de modules qui casse au chargement selon l'ordre d'évaluation.
    // Sans cela, une reconnexion dans le même onglet garderait les droits du
    // compte précédent.
    this.userService.on(UserEvent.loggedOut, () => this.clear());
  }

  // Appelée par le guard de /app : les appels concurrents partagent la même
  // promesse, sinon chaque route enfant relancerait un /me.
  async ensureLoaded(): Promise<void> {
    if (this.current) return;
    if (!this.loading) {
      this.loading = this.load().finally(() => (this.loading = null));
    }
    return this.loading;
  }

  private async load(): Promise<void> {
    const resp = await this.adminUserService.me();
    if (resp.status === ServerStatus.ok) {
      this.current = resp.body;
    } else {
      // Un /me en échec ne doit pas ouvrir les écrans par défaut.
      this.current = {userName: null, roles: [], permissions: [], knownPermissions: [], assignableRoles: []};
    }
  }

  clear() {
    this.current = null;
  }

  get userName(): string | null {
    return this.current?.userName ?? null;
  }

  get roles(): string[] {
    return this.current?.roles ?? [];
  }

  get assignableRoles(): string[] {
    return this.current?.assignableRoles ?? [];
  }

  get hasNoAccess(): boolean {
    return (this.current?.permissions.length ?? 0) === 0;
  }

  has(permission: string): boolean {
    const permissions = this.current?.permissions ?? [];
    return permissions.includes(ALL_PERMISSIONS) || permissions.includes(permission);
  }
}
